import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Mail, Lock, User, Phone, Loader2, ArrowRight } from 'lucide-react';

const RegisterPage = () => {
  const { language } = useLanguage();
  const { register, error: authError } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const content = {
    en: {
      title: "Create Account",
      subtitle: "Join Canada English Life and start learning today",
      name: "Full Name",
      email: "Email Address",
      phone: "Phone Number (Optional)",
      password: "Password",
      confirmPassword: "Confirm Password",
      register: "Create Account",
      hasAccount: "Already have an account?",
      login: "Sign in here",
      passwordMismatch: "Passwords do not match",
      passwordWeak: "Password must be at least 6 characters",
      error: "Registration failed. Please try again.",
      backToHome: "Back to Home"
    },
    ar: {
      title: "إنشاء حساب",
      subtitle: "انضم إلى Canada English Life وابدأ التعلم اليوم",
      name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      phone: "رقم الهاتف (اختياري)",
      password: "كلمة المرور",
      confirmPassword: "تأكيد كلمة المرور",
      register: "إنشاء حساب",
      hasAccount: "لديك حساب بالفعل؟",
      login: "سجل الدخول هنا",
      passwordMismatch: "كلمات المرور غير متطابقة",
      passwordWeak: "يجب أن تكون كلمة المرور 6 أحرف على الأقل",
      error: "فشل التسجيل. يرجى المحاولة مرة أخرى.",
      backToHome: "العودة للرئيسية"
    }
  };

  const t = content[language];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password.length < 6) {
      setError(t.passwordWeak);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t.passwordMismatch);
      return;
    }

    setLoading(true);

    try {
      await register(formData.email, formData.password, formData.name, formData.phone || null);
      navigate('/student'); // New users are students by default
    } catch (err) {
      setError(err.message || t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Back Link */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-6">
            <ArrowRight className={`w-4 h-4 ${language === 'ar' ? 'ml-2 rotate-180' : 'mr-2 rotate-180'}`} />
            {t.backToHome}
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.title}</h1>
          <p className="text-gray-600">{t.subtitle}</p>
        </div>

        {/* Register Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error Message */}
            {(error || authError) && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                {error || authError}
              </div>
            )}

            {/* Name Field */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-700 font-medium">
                {t.name}
              </Label>
              <div className="relative">
                <User className={`absolute top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={language === 'en' ? "John Doe" : "محمد أحمد"}
                  className={`${language === 'ar' ? 'pr-10 text-right' : 'pl-10'} h-12`}
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">
                {t.email}
              </Label>
              <div className="relative">
                <Mail className={`absolute top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className={`${language === 'ar' ? 'pr-10 text-right' : 'pl-10'} h-12`}
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            {/* Phone Field */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-gray-700 font-medium">
                {t.phone}
              </Label>
              <div className="relative">
                <Phone className={`absolute top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+966 5XX XXX XXXX"
                  className={`${language === 'ar' ? 'pr-10 text-right' : 'pl-10'} h-12`}
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-medium">
                {t.password}
              </Label>
              <div className="relative">
                <Lock className={`absolute top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className={`${language === 'ar' ? 'pr-10 text-right' : 'pl-10'} h-12`}
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 ${language === 'ar' ? 'left-3' : 'right-3'}`}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-gray-700 font-medium">
                {t.confirmPassword}
              </Label>
              <div className="relative">
                <Lock className={`absolute top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className={`${language === 'ar' ? 'pr-10 text-right' : 'pl-10'} h-12`}
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-lg mt-2"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t.register
              )}
            </Button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              {t.hasAccount}{' '}
              <Link to="/login" className="text-teal-600 hover:text-teal-700 font-semibold">
                {t.login}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
